import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { api } from "@/api";

/* Who is signed in, and which organization they are looking at.

   Never rendered signed-out on a guess: that flash, on a slow connection, is long enough
   to click through. A returning visitor starts from the last session this browser
   confirmed, so the app opens at once instead of behind a loading screen, and it is
   checked again in the background -- an ended session still lands on sign in, and every
   API call is authorised server-side regardless. Only a first visit waits. */

const SessionContext = createContext(null);

const ORG_KEY = "codenerve.org";
const SESSION_KEY = "codenerve.session";

/* Storage can be disabled or full; the session then simply is not remembered. */
function readCachedSession() {
  try {
    const cached = JSON.parse(localStorage.getItem(SESSION_KEY) || "null");
    if (cached?.user && cached?.profile) return { status: "in", user: cached.user, profile: cached.profile };
  } catch {
    /* fall through to a fresh check */
  }
  return { status: "loading", user: null, profile: null };
}

function writeCachedSession(session) {
  try {
    if (session) localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    else localStorage.removeItem(SESSION_KEY);
  } catch {
    /* not remembered; the next visit checks again */
  }
}
/* The website's own backend, which holds the website session, and its landing page. */
const WEBSITE_API = (import.meta.env.VITE_WEBSITE_API_URL || "https://codenerve.ai").replace(/\/$/, "");
const WEBSITE_HOME = import.meta.env.VITE_WEBSITE_URL || "https://codenerve.ai/";

export function SessionProvider({ children }) {
  const [state, setState] = useState(readCachedSession);
  const [orgId, setOrgId] = useState(() => localStorage.getItem(ORG_KEY) || null);

  const refresh = useCallback(async () => {
    try {
      const [{ user }, profile] = await Promise.all([api.get("/auth/me"), api.get("/users/me")]);
      writeCachedSession({ user, profile });
      setState({ status: "in", user, profile });
      return { user, profile };
    } catch (error) {
      /* Only the server refusing the session ends it. A network blip or a server error
         keeps the session this tab has; a first visit with nothing to keep goes to sign in. */
      const refused = error?.status === 401 || error?.status === 403;
      setState((current) => {
        if (!refused && current.status === "in") return current;
        writeCachedSession(null);
        return { status: "out", user: null, profile: null };
      });
      return null;
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const organizations = state.profile?.organizations ?? [];
  /* A remembered organization the account no longer belongs to is not a valid choice, so
     the first membership wins rather than leaving every screen requesting a 404. */
  const organization =
    organizations.find((item) => item.id === orgId) ?? organizations[0] ?? null;

  useEffect(() => {
    if (organization && organization.id !== orgId) {
      setOrgId(organization.id);
      localStorage.setItem(ORG_KEY, organization.id);
    }
  }, [organization, orgId]);

  const value = useMemo(
    () => ({
      ...state,
      organizations,
      organization,
      orgId: organization?.id ?? null,
      refresh,
      selectOrganization: (id) => {
        localStorage.setItem(ORG_KEY, id);
        setOrgId(id);
      },
      /* Two sessions exist until they are merged: the app's, and the website's own. Ending
         only the app's left the website signed in, so it showed "Dashboard" instead of
         "Sign in", and its "Start a free review" bounced off the app and back. Both are
         ended here, server-side, and either failing must not keep the person signed in
         to the other. */
      signOut: async () => {
        try {
          await Promise.allSettled([
            api.post("/auth/logout"),
            fetch(`${WEBSITE_API}/api/auth/logout`, {
              method: "POST",
              credentials: "include",
              headers: { "content-type": "application/json" },
              body: "{}",
              signal: AbortSignal.timeout(8000),
            }),
          ]);
        } finally {
          writeCachedSession(null);
          localStorage.removeItem(ORG_KEY);
          window.location.replace(WEBSITE_HOME);
        }
      },
    }),
    [state, organizations, organization, refresh],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  const value = useContext(SessionContext);
  if (!value) throw new Error("useSession must be used inside a SessionProvider");
  return value;
}

/* The organization path segment every dashboard call needs. */
export function useOrgPath() {
  const { orgId } = useSession();
  return orgId ? `/organizations/${orgId}` : null;
}
